import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import type { AssessmentRecord } from '../../models';
import { getAiLabelState } from '../../utils/assessment.utils';

/** Which EU AI Act (Art. 50) label to render.
 *  - `generated` — content produced by AI and not yet edited by a human.
 *  - `modified`  — content that has been edited (human touched an AI draft,
 *                  or AI has modified human content).
 *  - `ai`        — generic "AI involved" marker; used where the specific
 *                  state can't be computed (e.g. non-assessment surfaces). */
type AiLabelState = 'generated' | 'modified' | 'ai';

/** Visual footprint.
 *  - `compact` — circular "AI" badge; always uses the generic `label-ai-*`
 *                asset regardless of `state`. Use in card/table/inline
 *                contexts where a full pill wouldn't fit.
 *  - `full`    — rectangular "AI Generated" / "AI Modified" pill; picks
 *                the asset by `state`. Use in detail headers / PDF. */
type AiLabelVariant = 'compact' | 'full';

/**
 * EU AI Act (Article 50) compliant provenance label.
 *
 * Deployers of generative AI must label output as AI-generated or
 * AI-manipulated ("modified"). Once a human has meaningfully reviewed
 * the content (`review_status === 'REVIEWED'`), the label is dropped —
 * the record is treated as human editorial output.
 *
 * Two independent inputs drive what renders:
 *   - `state`   — explicit semantic override (default `'ai'`).
 *   - `record`  — when set, overrides `state` by computing the semantic
 *                 from `review_status` + `edits_count` via
 *                 `getAiLabelState`. Returns `null` when the record is
 *                 REVIEWED; callers should not render the component at
 *                 all in that case, but if they do we fall back to the
 *                 explicit `state` value.
 *
 * Colour-mode: two SVGs (black-pill for light bg, white-pill for dark bg)
 * both render into the DOM; visibility swap is CSS-driven via
 * `:host-context([data-theme='dark'])` so first paint is correct without
 * any JS observation of the theme attribute.
 */
@Component({
  selector: 'app-ai-label',
  imports: [TranslocoModule],
  template: `
    <span
      class="ai-label"
      [class.ai-label--compact]="variant() === 'compact'"
      [class.ai-label--full]="variant() === 'full'"
      [attr.aria-label]="ariaKey() | transloco"
      role="img"
    >
      <img
        [src]="lightSrc()"
        alt=""
        aria-hidden="true"
        class="ai-label__icon ai-label__icon--light"
      />
      <img
        [src]="darkSrc()"
        alt=""
        aria-hidden="true"
        class="ai-label__icon ai-label__icon--dark"
      />
    </span>
  `,
  styleUrl: './ai-label.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiLabelComponent {
  state = input<AiLabelState>('ai');
  variant = input<AiLabelVariant>('compact');
  record = input<AssessmentRecord | undefined>(undefined);

  private readonly resolvedState = computed<AiLabelState>(() => {
    const r = this.record();
    if (r) {
      const derived = getAiLabelState(r);
      if (derived) return derived;
    }
    return this.state();
  });

  private readonly fileStem = computed(() => {
    // Compact is always the generic circular "AI" mark — the pill
    // variants only carry the semantic word ("Generated" / "Modified").
    if (this.variant() === 'compact') return 'ai';
    const s = this.resolvedState();
    if (s === 'generated') return 'ai-generated';
    if (s === 'modified') return 'ai-modified';
    return 'ai';
  });

  protected readonly lightSrc = computed(
    () => `assets/ai-labels/label-${this.fileStem()}-black.svg`
  );
  protected readonly darkSrc = computed(
    () => `assets/ai-labels/label-${this.fileStem()}-white.svg`
  );

  protected readonly ariaKey = computed(() => {
    const s = this.resolvedState();
    if (s === 'generated') return 'ai_label.aria_generated';
    if (s === 'modified') return 'ai_label.aria_modified';
    return 'ai_label.aria_ai';
  });
}
